import { useMemo } from 'react'
import { BellRing } from 'lucide-react'
import type { AdminColumn } from '@/components/DataTable/types'
import { RegistryCrudTable, type RegField, type RegRow } from '@/components/RegistryCrudTable'
import { SystemConfigForm } from './SystemConfigForm'
import { SYS_STR as S } from './systemConfigApi'

/**
 * A24.06 / A24.08 — endi JONLI (soxta ma'lumot yo'q):
 *   • A24.06 Push sertifikatlari — admin-boshqariladigan CRUD reyestr
 *     (`push_certs`, migration 000249): APNs/FCM/WebPush hisob ma'lumoti
 *     metama'lumoti (platforma, sertifikat nomi, muddat, holat). Real CRUD
 *     `/admin/settings/push-certs`.
 *   • A24.08 CDN — sof konfiguratsiya (app_settings `cdn_*`), boshqa A24 config
 *     ekranlar kabi `/admin/settings/system` orqali round-trip qiladi.
 */

interface PushCertRow extends RegRow {
  id: number
  platform: string
  cert_name: string
  expires_at: string
  status: string
  created_at: string
}

/** A24.06 — Push sertifikatlari (APNs/FCM/WebPush). */
export function PushCertificates() {
  const columns = useMemo<AdminColumn<PushCertRow>[]>(
    () => [
      { key: 'platform', header: S.cPlatform, type: 'text', width: 140 },
      { key: 'cert_name', header: S.cCertName, type: 'text', minWidth: 180 },
      { key: 'expires_at', header: S.cExpires, type: 'text', width: 150 },
      {
        key: 'status',
        header: S.cStatus,
        type: 'enum',
        width: 140,
        filterable: true,
        options: [
          { value: 'active', label: 'active', status: 'active' },
          { value: 'expiring', label: 'expiring', status: 'pending' },
          { value: 'expired', label: 'expired', status: 'deleted' },
          { value: 'revoked', label: 'revoked', status: 'review' },
        ],
      },
    ],
    [],
  )
  const fields = useMemo<RegField[]>(
    () => [
      {
        name: 'platform',
        placeholder: S.cPlatform,
        type: 'select',
        options: [
          { value: 'apns', label: 'APNs' },
          { value: 'fcm', label: 'FCM' },
          { value: 'webpush', label: 'WebPush' },
        ],
      },
      { name: 'cert_name', placeholder: S.cCertName },
      { name: 'expires_at', placeholder: S.cExpires, type: 'date' },
      {
        name: 'status',
        placeholder: S.cStatus,
        type: 'select',
        options: [
          { value: 'active', label: 'active' },
          { value: 'expiring', label: 'expiring' },
          { value: 'expired', label: 'expired' },
          { value: 'revoked', label: 'revoked' },
        ],
      },
    ],
    [],
  )
  return (
    <RegistryCrudTable<PushCertRow>
      screenId="A24.06"
      title={S.pushTitle}
      description={S.pushDesc}
      addLabel={S.addPushCert}
      path="/admin/settings/push-certs"
      listKey="certs"
      searchKeys={['platform', 'cert_name', 'status']}
      requiredField="cert_name"
      fields={fields}
      columns={columns}
      icon={<BellRing size={28} strokeWidth={1.5} />}
    />
  )
}

/** A24.08 — CDN sozlamalari (edge/pull-zonalar) — app_settings cdn_* config. */
export function CdnSettings() {
  return (
    <SystemConfigForm
      screenId="A24.08"
      title={S.cdnTitle}
      description={S.cdnDesc}
      sections={[
        {
          title: S.cdnSecCore,
          fields: [
            { key: 'cdn_enabled', label: S.cdnEnabled, type: 'bool', hint: S.cdnEnabledHint, def: 'false' },
            {
              key: 'cdn_provider',
              label: S.cdnProvider,
              type: 'select',
              def: 'none',
              options: [
                { value: 'none', label: 'none' },
                { value: 'cloudflare', label: 'Cloudflare' },
                { value: 'bunny', label: 'Bunny' },
                { value: 'cloudfront', label: 'CloudFront' },
                { value: 'fastly', label: 'Fastly' },
                { value: 'other', label: 'other' },
              ],
            },
            { key: 'cdn_base_url', label: S.cdnBaseUrl, mono: true, placeholder: 'https://cdn.example.com' },
            { key: 'cdn_zone', label: S.cdnZone, mono: true },
            { key: 'cdn_notes', label: S.cdnNotes, type: 'textarea' },
          ],
        },
      ]}
    />
  )
}
